// Copyright (c) Hamilton Manufacturing Corp.

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Icon from './Icon';
import Button from './Button';
import { HamQuest } from '../../../App';
import { QuestRoute } from '../api/QuestRoutes';

export const FormContext = React.createContext();

const I = styled(Icon)`
    margin-left: 0.5em;
`;
const Error = styled.div`
    font-size: 0.7em;
    padding: 0.2em 0.5em;
    color: ${({ theme }) => theme.error};
`
const Frm = styled.form`
    display: flex;
    flex-direction: ${({ direction }) => direction};
`


const Form = ({ inputs, onSubmit, displayform, row, children, ...props }) => {

    const [initial, setInitial] = useState(inputs)
    const [values, setValues] = useState(inputs)
    useEffect(() => {
        // Potential Issues here on updating form inputs.
        if (JSON.stringify(inputs) === JSON.stringify(initial)) return;
        setValues(inputs)
    }, [inputs])

    const [errors, setErrors] = useState(
        Object.fromEntries(
            Object.entries(inputs).map(([key, val]) => [key, val === '' ? 'unTouched' : false])
        )
    )
    const [submitting, setSubmitting] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [editing, setEditing] = useState(displayform ? false : true);
    const [formError, setFormError] = useState('')

    let hasSubmit = false;

    useEffect(() => {
        setDirty(
            Object
                .keys(errors)
                .find(v => errors[v] === true || errors[v] === 'unTouched')
        )
    }, [errors])

    const handleSubmit = async e => {
        e.preventDefault();
        if (dirty) return;
        setSubmitting(true);

        try {
            if (props.localForm) {
                onSubmit(values)
                setSubmitting(false)
                setEditing(displayform ? false : true)
            } else {
                onSubmit(values)
                    .catch(err => {
                        setFormError(err.errorMessage || err.blob || 'Oops, something went wrong.')
                    })
                    .finally(() => {
                        setSubmitting(false)
                        setEditing(displayform ? false : true)
                        HamQuest.removeHeaderKey('hamGuard');
                    })
            }
            setInitial(values);
        }
        catch (er) {
            setFormError(er.blob || 'Oops, something went wrong.')
            setSubmitting(false)
            setEditing(displayform ? false : true)
            setValues(initial);
            HamQuest.removeHeaderKey('hamGuard');
        }
    }

    const createSubmitButton = () => (
        <Button disabled={submitting || dirty}>
            {
                props.submitButton ?
                    (submitting ? (props.submittingButton || 'Submitting') : props.submitButton) :
                    (submitting ? 'Submitting' : 'Submit')
            }
            {submitting ? <I icon='spinner' /> : null}
        </Button>
    )

    const childrenWithProps = React.Children.map(children, child => {
        if (React.isValidElement(child)) {
            if (child.props.type === 'submit') hasSubmit = true;
            return child.props.type === 'submit' ? createSubmitButton() : child;
        }
        return child;
    });

    return (
        <Frm
            onSubmit={(e) => handleSubmit(e)}
            direction={row === true ? 'row' : 'column'}
            {...props}
        >
            <FormContext.Provider value={{ values, submitting, editing, errors, setValues, setErrors }}>
                {/* {childrenWithProps} */}
                {typeof children === 'function' ? children({ values, submitting, editing, errors, setValues, setErrors }) : childrenWithProps}
                <Error>{formError}</Error>
                {
                    !hasSubmit ?
                        (!displayform || editing) ?
                            createSubmitButton() : null
                        : null
                }
                {
                    (displayform) ?
                        <Button
                            onClick={(e) => {
                                e.preventDefault();
                                setEditing(!editing);
                                setValues(initial);
                            }}>
                            {!editing ? props.editbutton || 'Edit' : props.cancelbutton || 'Cancel'}
                        </Button> : null
                }
            </FormContext.Provider>
        </Frm>
    );
}

export default Form;

/*
Examples
<Forms
    inputs={{
        cardNumber: 1354351348351486,
        testBox: true,
        checkboxTest: [1, 3, 5],
        radioTest: 2,
        selectTest: 13
    }}
    onSubmit={(e) =>
        HamQuest.Get('/giftcards/balance/' + e.cardNumber)
            .then(res => Modal.ShowModal({
                title: 'Card Balance',
                body:
                    <Col>
                        <div>Status: {res.testMessage}</div>
                        <div>Balance: {currency.format(res.balance ?? 0)}</div>
                    </Col>
            }))
    }
    submitbutton="Check"
    displayform // Edit button for display forms
>
    <CheckBox
        label='Test Box'
        name='testBox'
    />
    <Input
        label='Check Balance:'
        name='cardNumber'
        sanitize={(e) => e.replace(/\D/, '')}
        maxLength={16}
        validate={RegexTest.creditCard}
        placeholder='Card Number'
        required
    />
    <ListItems
        name='checkboxTest'
        type='checkbox'
        heading='Checkbox Test'
        labels={[1, 2, 3, 4, 5]}
        values={[1, 2, 3, 4, 5]}
    />
    <ListItems
        name='radioTest'
        heading='Radio Test'
        labels={[1, 2, 3, 4, 5]}
        values={[1, 2, 3, 4, 5]}
    />
    <Select
        name='selectTest'
        label='Select Test'
        options={[
            {
                label: 'Cool',
                value: 10
            }, {
                label: 'Test',
                value: 11
            }, {
                label: 'Drop',
                value: 12
            }, {
                label: 'Down',
                value: 13
            },
        ]}
    />
</Forms>
*/